// Create file: src/pages/contests/running/SelectedTeamGolfRunning.js
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { formatNumber } from '../../../../utils/helpers'; // Keep formatNumber for salary
import './SelectedTeamGolfRunning.css'; // Use Golf-specific CSS

const SelectedTeamGolfRunning = ({ team, contest }) => {

  // --- Hooks must be called before conditional returns ---

  // Player Data Map (Finish, Score, Thru, Salary, Status)
  const playerDataMap = useMemo(() => {
    const map = {};
    contest.players.forEach(player => {
      map[player.name] = {
        finish: player.finish, // Keep finish for active/eliminated status
        score: player.sportSpecificData?.score ?? null,
        thru: player.sportSpecificData?.thru ?? null, // Add thru
        salary: player.salary,
        // Ensure status is included
        status: player.sportSpecificData?.status ?? null
      };
    });
    // DEBUG: Log the map once
    return map;
  }, [contest.players]);

  // Calculate Total Team Score
  const totalTeamScore = useMemo(() => {
    if (!team) return null;
    let total = 0;
    let playersContributing = 0;

    // Captain Score
    const captainData = playerDataMap[team.team.captain];
    if (captainData && typeof captainData.score === 'number') {
       // Assuming no captain bonus for score unless specified
      total += captainData.score; 
      playersContributing++;
    }

    // Flex Player Scores
    team.team.flexPlayers.forEach(player => {
      const playerData = playerDataMap[player];
      if (playerData && typeof playerData.score === 'number') {
        total += playerData.score;
        playersContributing++;
      }
    });
    // Return 0 if no players contributed, otherwise the calculated score
    return playersContributing > 0 ? total : 0; 
  }, [playerDataMap, team]);

  // --- Conditional return ---
  if (!team) return null;

  // --- Logic that depends on team ---

  // Format Thru status for display
  const formatThru = (thru) => {
    if (thru === null || thru === 0 || thru === undefined || thru === '0') return '-'; // Not started or no data
    if (thru === 18 || thru === 'F') return 'F'; // Finished 18 holes
    // Note: CUT handling is now done directly in the JSX
    return thru.toString(); 
  };

  // Format score for display
  const formatScore = (score) => {
    if (score === null) return '-';
    if (score === 0) return 'E';
    return score > 0 ? `+${score}` : score.toString();
  };

  // Get salaries for display
  const captainSalary = playerDataMap[team.team.captain]?.salary || 0;
  const flexPlayerSalaries = team.team.flexPlayers.map(
    player => playerDataMap[player]?.salary || 0
  );

  return (
    <div className="selected-team-container-golf">
      <div className="selected-team-header">
        <h2>{team.entryName || team.username}'s Team</h2>
      </div>
      <div className="selected-team-content">
        <table className="selected-team-table">
          <thead>
            <tr><th>Pos</th><th>Name</th><th className="show-on-desktop">Salary</th><th className="thru-header">Thru</th><th className="score-header">Score</th></tr>
          </thead>
          <tbody>
            {(() => { // Captain Row
              const captainName = team.team.captain;
              const captainData = playerDataMap[captainName];
              const captainThru = captainData?.thru;
              const captainScore = captainData?.score ?? null;
              const captainStatus = captainData?.status;
              
              // DEBUG: Log captain status check
              const isCaptainCut = captainStatus === 'cut';
              const captainStatusClass = isCaptainCut ? 'player-eliminated' : 'player-active';
              
              return (
                <tr><td><span className="position-label position-label--cpt">CPT*</span></td><td className={`captain-cell ${captainStatusClass}`}>{team.team.captain}</td><td className={`show-on-desktop ${captainStatusClass}`}>${formatNumber(captainSalary)}</td><td className={`${captainStatusClass} thru-value`}>{isCaptainCut ? 'CUT' : formatThru(captainThru)}</td><td className={`${captainStatusClass} score-value`}>{formatScore(captainScore)}</td></tr>
              );
            })()}
            {team.team.flexPlayers.map((player, index) => { // Flex Player Rows
              const playerData = playerDataMap[player];
              const playerThru = playerData?.thru;
              const playerScore = playerData?.score ?? null;
              const playerStatus = playerData?.status;
              const playerSalary = flexPlayerSalaries[index];

              // DEBUG: Log flex player status check
              const isPlayerCut = playerStatus === 'cut';
              const playerStatusClass = isPlayerCut ? 'player-eliminated' : 'player-active';

              return (
                <tr key={index}><td><span className="position-label">FLEX</span></td><td className={`flex-cell ${playerStatusClass}`}>{player}</td><td className={`show-on-desktop ${playerStatusClass}`}>${formatNumber(playerSalary)}</td><td className={`${playerStatusClass} thru-value`}>{isPlayerCut ? 'CUT' : formatThru(playerThru)}</td><td className={`${playerStatusClass} score-value`}>{formatScore(playerScore)}</td></tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="totals-row"><td className="totals-label" colSpan="2">Totals:</td><td className="show-on-desktop totals-value">${formatNumber(team.teamSalary)}</td><td></td><td className="totals-value">{formatScore(totalTeamScore)}</td></tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

SelectedTeamGolfRunning.propTypes = {
  team: PropTypes.object.isRequired,
  contest: PropTypes.shape({
     players: PropTypes.arrayOf(PropTypes.shape({
       // ... other player props
       sportSpecificData: PropTypes.shape({ 
         score: PropTypes.number,
         thru: PropTypes.oneOfType([PropTypes.number, PropTypes.string]) // Can be number or 'F'
       })
     })).isRequired
     // ... other contest props
   }).isRequired
};

export default SelectedTeamGolfRunning;